/**
 * ② 対象データの履歴一覧 (マスタ変更履歴 SBI 7)。
 *
 * 1 つの対象 (`entity_key`) の履歴を新しい順に表示する。削除済みの対象も引ける。
 * 履歴が 1 件も無い対象は 200 + 空配列で返るため、エラーではなく「履歴はありません」を出す。
 * 行を押すと ③ の URL (`entity_key` + `rev`) へ遷移する。
 *
 * 権限: 画面の出し分けは利便性のため。対象のマスタ種別の機能を持たない場合は、API を呼ばずに
 * 説明を出す (バックエンドは 403)。種別が `entity_key` から判定できない場合は API に任せる。
 */
import { useMemo } from 'react';
import type React from 'react';

import { MasterHistoryClient } from '../api/masterHistory';
import type { MasterHistoryListEntry } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { useMasterHistoryPages } from '../hooks/useMasterHistoryPages';
import { buildDetailPath, buildListPath, masterTypeOfEntityKey } from '../pages/masterHistoryView';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';

import { MasterHistorySourceBackButton } from './MasterHistorySourceBackButton';
import { MasterHistoryTable } from './MasterHistoryTable';

export interface MasterHistoryEntityScreenProps {
  config: AppConfig;
  entityKey: string;
  /** 対象のマスタ種別の機能を持たないとき true (API を呼ばない) */
  forbidden: boolean;
  /** 不正な `rev` で ③ を開けなかったときの案内を出す */
  invalidRev: boolean;
  /** ② / ① の URL へ移る (react-router の navigate を呼び出し側から渡す) */
  onNavigate: (path: string) => void;
  /** ヘッダーのタブから入ったとき true (「閉じる」「遷移元へ戻る」を出さない) */
  hideSourceBack?: boolean;
}

export const FORBIDDEN_TYPE_MESSAGE =
  'このデータの変更履歴を表示する権限がありません。管理者に確認してください';

export function MasterHistoryEntityScreen(
  props: MasterHistoryEntityScreenProps,
): React.JSX.Element {
  const { config, entityKey, forbidden, invalidRev, onNavigate, hideSourceBack = false } = props;
  const { getIdToken } = useAuth();
  const client = useMemo(
    () => new MasterHistoryClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const pages = useMasterHistoryPagesFor(client, entityKey, forbidden);
  const errorMessage = pages.error === null ? null : describeApiErrorMessage(pages.error);

  return (
    <section
      className="section section--page list-page"
      data-testid="master-history-entity"
      data-entity-key={entityKey}
    >
      <h2>対象データの履歴</h2>
      <div className="sort-filter-bar">
        <button
          type="button"
          onClick={() => onNavigate(buildListPath())}
          data-testid="master-history-entity-back"
        >
          変更一覧へ戻る
        </button>
        <MasterHistorySourceBackButton
          masterType={masterTypeOfEntityKey(entityKey)}
          forbidden={forbidden}
          hidden={hideSourceBack}
          onNavigate={onNavigate}
          testId="master-history-entity-back-source"
        />
      </div>
      {invalidRev && (
        <p role="status" data-testid="master-history-rev-notice">
          指定された版を表示できないため、対象データの履歴一覧を表示しています
        </p>
      )}
      {forbidden ? (
        <p role="alert" data-testid="master-history-entity-forbidden">
          {FORBIDDEN_TYPE_MESSAGE}
        </p>
      ) : (
        <MasterHistoryTable
          items={pages.items}
          loading={pages.loading}
          loadingMore={pages.loadingMore}
          errorMessage={errorMessage}
          hasMore={pages.hasMore}
          showTarget={false}
          onSelect={(entry: MasterHistoryListEntry) =>
            onNavigate(buildDetailPath(entry.entity_key, entry.version))
          }
          onLoadMore={pages.loadMore}
          onReload={pages.reload}
          testIdPrefix="master-history-entity"
        />
      )}
    </section>
  );
}

/** 権限がないときは取得しない (フックは条件付きで呼べないため、取得関数側で止める) */
function useMasterHistoryPagesFor(
  client: MasterHistoryClient,
  entityKey: string,
  forbidden: boolean,
) {
  const fetchPage = useMemo(
    () => (nextToken: string | undefined) =>
      forbidden
        ? Promise.resolve({ items: [], count: 0 })
        : client.listByEntity(entityKey, { nextToken }),
    [client, entityKey, forbidden],
  );
  return useMasterHistoryPages(fetchPage, `${entityKey}|${forbidden}`);
}
